import React from 'react';

import { useIntl } from '@kamernet/core/Intl';
import { Locale } from '@kamernet/core/Intl/types';
import {
  ListingsSearchCategory,
  getMainListingsSearchCategory,
} from '@kamernet/core/Routing/routes/ListingsSearch';

import { ListingsSearchContext } from '../ListingsSearchContext';

export function usePageTitle(): { pageTitle: string } {
  const { allFilters, totalListingsCount } = ListingsSearchContext.useContext();
  const { T, locale } = useIntl();

  return React.useMemo(() => {
    const mainSearchCategory = getMainListingsSearchCategory(
      allFilters.searchCategories,
    );

    const location =
      allFilters.streetName || allFilters.cityName || T('LBL_NETHERLANDS');

    const mapEN: Record<ListingsSearchCategory, string> = {
      [ListingsSearchCategory.Empty]: '',
      [ListingsSearchCategory.Generic]: `${totalListingsCount} Rental properties in ${location}`,
      [ListingsSearchCategory.Room]: `${totalListingsCount} Rooms for rent in ${location}`,
      [ListingsSearchCategory.Apartment]: `${totalListingsCount} Apartments for rent in ${location}`,
      [ListingsSearchCategory.Studio]: `${totalListingsCount} Studios for rent in ${location}`,
      [ListingsSearchCategory.Antisquat]: `${totalListingsCount} Anti-squats for rent in ${location}`,
      [ListingsSearchCategory.StudentHousing]: `${totalListingsCount} Student accommodations in ${location}`,
      [ListingsSearchCategory.FurnishedApartment]: `${totalListingsCount} Furnished apartments for rent in ${location}`,
      [ListingsSearchCategory.ShortTermRental]: `${totalListingsCount} Short term rentals in ${location}`,
      [ListingsSearchCategory.LongTermRental]: `${totalListingsCount} Long term rentals in ${location}`,
    };

    const mapNL: Record<ListingsSearchCategory, string> = {
      [ListingsSearchCategory.Empty]: '',
      [ListingsSearchCategory.Generic]: `${totalListingsCount} Huurwoningen in ${location}`,
      [ListingsSearchCategory.Room]: `${totalListingsCount} Kamers te huur in ${location}`,
      [ListingsSearchCategory.Apartment]: `${totalListingsCount} Appartementen te huur in ${location}`,
      [ListingsSearchCategory.Studio]: `${totalListingsCount} Studios te huur in ${location}`,
      [ListingsSearchCategory.Antisquat]: `${totalListingsCount} Anti-kraaks te huur in ${location}`,
      [ListingsSearchCategory.StudentHousing]: `${totalListingsCount} Studentenwoningen in ${location}`,
      [ListingsSearchCategory.FurnishedApartment]: `${totalListingsCount} Gemeubileerde appartementen te huur in ${location}`,
      [ListingsSearchCategory.ShortTermRental]: `${totalListingsCount} Tijdelijke woning huren in ${location}`,
      [ListingsSearchCategory.LongTermRental]: `${totalListingsCount} Lange termijn huren in ${location}`,
    };

    return {
      pageTitle:
        locale === Locale.en
          ? mapEN[mainSearchCategory]
          : mapNL[mainSearchCategory],
    };
  }, [
    T,
    allFilters.cityName,
    allFilters.searchCategories,
    allFilters.streetName,
    locale,
    totalListingsCount,
  ]);
}
